<template>
  <Card class="pa-2 subheading" :style="{ height: cardHeight }">
    <div id="info-header">
      <span class="subheading">{{ $t('Inverter') }}</span> {{ $t('Live Data') }}
    </div>
    <DataTable class="inv-data-card" :headers="computedInvHeaders" :items="invData"
      :style="{ height: tableHeight }"
      :isPaginationFalse="true">
      <template v-slot:firstCol="{ text }">
        <td>
          <v-layout row wrap align-center class="first-col-width">
            <div class="text__width">
              {{ text.name }}
            </div>
            <div>
              <v-icon
                :color="text.ac_power == undefined ? 'grey' : ( text.ac_power <= 0 ? 'red' : 'green' )"
                class="icon-class" size="10">
                circle</v-icon>
            </div>
          </v-layout>
        </td>
      </template>
    </DataTable>
  </Card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import LiveDb from '@/services/LiveDb.js'
import { mapGettersMixin } from '@/util/mixin/index'
import { liveDeviceMqttMixin } from '@/util/mixin/liveDeviceMqtt'

export default {
  mixins: [ mapGettersMixin, liveDeviceMqttMixin ],
  props: {
    parameter: {
      type: Array,
      default: function() {
        return []
      }
    },
    parameterNameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    nameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    cardHeight: {
      type: String,
      default: 'calc(100vh - 570px)'
    },
    tableHeight: {
      type: String,
      default: 'calc(100vh - 623px)'
    }
  },
  data() {
    return {
      invHeaders: [],
      invData: [],
      liveInverterData: {},
      computedInverterData: []
    }
  },
  computed: {
    ...mapGetters([ 'getSelectedPlantId' ]),
    computedInvHeaders() {
      const cloneHeader = _.cloneDeep(this.invHeaders)
      return cloneHeader.map(el => {
        el['text'] = this.$t(el.text)
        return el
      })
    }
  },
  async mounted() {
    this.initialStateValues()
    await this.loadInverterForPlant(this.getSelectedPlantId)
  },
  methods: {
    initialStateValues() {
      this.initialHeader()
      this.invData = []
      this.liveInverterData = {}
      this.computedInverterData = []
    },
    isFloat(value) {
      if (
        typeof value === 'number' &&
        !Number.isNaN(value) &&
        !Number.isInteger(value)
      ) {
        return true;
      }

      return false;
    },
    toThreeDecimalPlaces(props) {
      if (props !== undefined && props !== null) {
        if (this.isFloat(Number(props))) {
          return props == 0 ? 0 : Number.parseFloat(props).toFixed(3)
        } else {
          return props
        }
      } else {
        return 'No Data'
      }
    },
    async getInvGeneration(
      plantId = this.getSelectedPlantId,
      loadRequest = null
    ) {
      if (!this.isLiveDevicePlantCurrent(plantId)) return false

      const refreshRequest = this.beginLiveDeviceRefresh(plantId)
      const computedInverterData = await this.computedInvData(plantId)
      if (
        !this.isLiveDeviceRequestCurrent(refreshRequest) ||
        (loadRequest && !this.isLiveDeviceLoadCurrent(loadRequest))
      ) return false

      this.computedInverterData = computedInverterData

      const computedMap = this.computedInverterData.reduce((acc, curr) => {
        acc[curr.id] = curr
        return acc
      }, {})

      if (this.invData.length === 0) {
        this.initializeInverterRows(computedMap)
      } else {
        this.invData.forEach(row => {
          const computed = computedMap[row.id]
          if (computed) {
            this.$set(row, 'maxMinusMinEnergy', this.toThreeDecimalPlaces(computed.maxMinusMinEnergy))
          }
        })
      }
      return true
    },
    initializeInverterRows(computedMap = {}) {
      this.invData = Object.keys(this.parameterNameMap).map(idConfigured => {
        const newDataObj = {
          id: idConfigured,
          name: this.nameMap[idConfigured]
        }

        const inverterData = this.liveInverterData[idConfigured]
        if (inverterData) {
          this.parameter.forEach(key => {
            if (Object.prototype.hasOwnProperty.call(inverterData, key)) {
              newDataObj[key] = this.toThreeDecimalPlaces(inverterData[key])
            }
          })
        }

        const computed = computedMap[idConfigured]
        if (computed) {
          newDataObj.maxMinusMinEnergy = this.toThreeDecimalPlaces(computed.maxMinusMinEnergy)
        }

        return newDataObj
      })

      if (this.invData.length > 0) {
        this.mapHeaderHandler()
      }
    },
    async loadInverterForPlant(plantId) {
      const loadRequest = this.beginLiveDeviceLoad(plantId)
      let liveInverterData = {}

      try {
        liveInverterData = await this.liveInvData(plantId)
      } catch (error) {
        if (this.isLiveDeviceLoadCurrent(loadRequest)) {
          console.error('Error loading Live Dashboard inverter data', error)
        }
      }

      if (!this.isLiveDeviceLoadCurrent(loadRequest)) return

      this.liveInverterData = liveInverterData
      this.initializeInverterRows()
      this.setIntervalWithoutDelay(
        () => this.getInvGeneration(plantId, loadRequest),
        60000
      )
      this.mqttHandler(plantId)
    },
    async liveInvData(plantId = this.getSelectedPlantId) {
      let invParamJson = {}
      this.parameter.forEach(key => {
        invParamJson[key] = key
      })
      const invParam = Buffer.from(JSON.stringify(invParamJson)).toString('base64')
      // Live data query
      const response = await LiveDb.getLiveDeviceData(plantId, 'inverter', invParam)
      return response.data.message || {}
    },
    async computedInvData(plantId = this.getSelectedPlantId) {
      const inverterParams = Buffer.from(JSON.stringify({
        'energy': 'maxMinusMinEnergy'
      })).toString('base64')

      // Computed data query
      const response = await LiveDb.getGenerationData(plantId, 'inverter', inverterParams)
      return response.data.message || []
    },
    mapHeaderHandler() {
      const header = []
      this.parameter.forEach(param => {
        const ob = {}
        const name = this.parameterNameMap[this.invData[0].id][0].config[param].name
        const unit = this.parameterNameMap[this.invData[0].id][0].config[param].unit ? this.parameterNameMap[this.invData[0].id][0].config[param].unit : ''
        ob['text'] = `${name} ${unit ? `(${unit})` : ''}`
        ob['value'] = param
        ob['align'] = 'center'
        header.push(ob)
      })
      this.initialHeader()
      this.invHeaders = [...this.invHeaders, ...header]
    },
    mqttHandler(plantId = this.getSelectedPlantId) {
      this.startLiveDeviceMqtt(
        'inverter',
        this.updateInverterFromMqtt,
        plantId
      )
    },
    updateInverterFromMqtt(mqttData) {
      if (!mqttData || Number(mqttData.ignore_status) === 1) return

      const index = this.invData.findIndex(
        inverter => String(inverter.id) === String(mqttData.device_id)
      )
      if (index === -1) return

      this.parameter.forEach(key => {
        this.$set(
          this.invData[index],
          key,
          this.toThreeDecimalPlaces(mqttData[key])
        )
      })
    },
    initialHeader() {
      this.invHeaders = [{
        text: 'INV Name',
        align: 'center',
        sortable: false,
        value: 'name',
        width: '20%'
      },
      {
        text: 'Generation (kWh)',
        align: 'center',
        value: 'maxMinusMinEnergy',
        sortable: false
      }
      ]
    }
  },
  watch: {
    async getSelectedPlantId(newVal) {
      this.resetLiveDeviceLifecycle()
      this.initialStateValues()
      if (newVal) await this.loadInverterForPlant(newVal)
    }
  }
}
</script>

<style scoped>
.icon-class {
  margin-bottom: 2px;
  margin-left: 12px;
}

.first-col-width {
  width: 150px;
  word-wrap: break-word;
}

#info-header span {
  border-bottom: 2px solid #417CDE;
  display: inline-block;
  vertical-align: top;
}

#info-header {
  padding-bottom: 16px;
}

.text__width {
  width: 120px;
}

</style>
<style>
.inv-data-card .v-table__overflow .v-table thead tr:first-child {
  position: sticky;
  top: 0px;
  z-index: 2;
  background: var(--v-primary-base);
  border-top: 1px solid #38405C;
}

.inv-data-card .v-table__overflow .v-table tbody td:first-child,
table.v-table thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--v-primary-base);
  border-right: 1px solid #38405C;
}

</style>
